import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import DatePicker from "react-datepicker";
import { Panel, PanelHeader, PanelBody, PanelFooter } from "../../components/panel/panel.jsx";
import { usePagoParams } from "./hooks/usePagoParams";
import { usePagoById, useSavePago, useDeletePago } from "../../hooks_cache/usePagos.js";
import { useComprobantesByIds } from "../../hooks_cache/useComprobantes.js";

import { toast } from "react-toastify";
import { parseISO, format } from "date-fns";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const tipo_pago = [
  { value: 1, label: "EFECTIVO" },
  { value: 2, label: "CHEQUE" },
  { value: 3, label: "TRANSFERENCIA" },
];

const Pago = () => {
  const navigate = useNavigate();
  const { empresas } = useSelector((state) => state.auth);
  const id_empresa = empresas[0].id || null;

  const { id_pago, ids_comprobantes } = usePagoParams();
  const [idsCompConsul, setIdsCompConsul] = useState(ids_comprobantes);

  const { mutateAsync: savePago, isPending: isPendingSavePago } = useSavePago();
  const { mutate: eliminarPago, isPending: isPendingEliminarPago } = useDeletePago();

  const { data: pago, isLoading: isLoadingPago } = usePagoById(id_pago);
  const { data: comprobantes } = useComprobantesByIds(idsCompConsul);

  const isEditing = !!id_pago;

  useEffect(() => {
    if (isPendingSavePago || isPendingEliminarPago || isLoadingPago) {
      Swal.fire({
        title: "Procesando solicitud...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else {
      Swal.close();
    }
  }, [isPendingSavePago, isPendingEliminarPago, isLoadingPago]);

  const [formData, setFormData] = useState({
    fecha_pago: new Date(),
    referencia: "",
    monto: 0,
    id_tipopago: 1,
    id_empresa: null,
    id_tercero: null,
    comprobantesPagados: [], // {id_comprobante, monto_aplicado}
  });

  useEffect(() => {
    if (isEditing && pago) {
      setIdsCompConsul(pago.comprobantesPagados.map((item) => item.id_comprobante));
      setFormData({
        id: pago.id,
        fecha_pago: pago.fecha_pago,
        referencia: pago.referencia,
        monto: pago.monto,
        id_tipopago: pago.id_tipopago,
        id_empresa: pago.id_empresa,
        id_tercero: pago.id_tercero,
        comprobantesPagados: pago.comprobantesPagados || [],
      });
    }
  }, [pago]);

  useEffect(() => {
    if (!isEditing && ids_comprobantes.length > 0) {
      const abonosIniciales = comprobantes.map((comp) => ({
        id_comprobante: comp.id,
        monto_aplicado: comp.pendiente,
      }));
      const monto = abonosIniciales.reduce((acc, item) => acc + item.monto_aplicado, 0);

      setFormData((prev) => ({
        ...prev,
        id_empresa: id_empresa,
        id_tercero: comprobantes?.[0]?.Tercero?.id || null,
        comprobantesPagados: abonosIniciales,
        monto: monto,
      }));
    }
  }, [ids_comprobantes, comprobantes]);

  const handleInputChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSave = async () => {
    const errores = formData.comprobantesPagados.filter((abono, index) => {
      const comprobante = comprobantes.find((c) => c.id === abono.id_comprobante);
      return abono.monto_aplicado > (comprobante?.totalPagar ?? 0);
    });

    if (errores.length > 0) {
      toast.error("❌ Hay abonos que superan el valor total del comprobante");
      return;
    }

    try {
      const nuevoPago = await toast.promise(savePago(formData), {
        pending: "Guardando pago...",
        success: "✅ Pago guardado correctamente",
        error: {
          render({ data }) {
            const msg = data?.response?.data?.error || data?.message || "Error desconocido";
            return `Error al guardar el pago:\n${msg}`;
          },
        },
      });

      navigate(-1);
    } catch (err) {
      console.error("Error al guardar:", err);
    }
  };

  const eliminarRegistroDialog = () => {
    MySwal.fire({
      title: <strong>Eliminar pago!</strong>,
      html: <p>Esta seguro que desea eliminar este registro?, posteriormente no se puede deshacer esta accion! </p>,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Si, estoy seguro.",
      showConfirmButton: true,
      allowOutsideClick: true,
    }).then((result) => {
      if (result.isConfirmed) {
        eliminarPago(id_pago);
        navigate(-1);
      }
    });
  };

  const deleteItem = (id_comprobante) => {
    const updated = formData.comprobantesPagados.filter((item, i) => item.id_comprobante !== id_comprobante);
    const montoTotal = updated.reduce((acc, cp) => acc + (cp.monto_aplicado || 0), 0);

    setFormData((prev) => ({
      ...prev,
      monto: montoTotal,
      comprobantesPagados: updated,
    }));
    setIdsCompConsul((prev) => prev.filter((id) => id !== id_comprobante));
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/">Dashboard</Link>
        </li>
        <li className="breadcrumb-item">
          <Link to="/venta/registro_pagos">Registro de pagos</Link>
        </li>
        <li className="breadcrumb-item active">Pago</li>
      </ol>
      <h1 className="page-header">Datos de pago</h1>

      <div className="row mb-3">
        <div className="col-xl-12">
          <Panel>
            <PanelHeader noButton={true}>
              {isEditing ? `Pago id: ${id_pago} | Creado por ${pago?.User?.name}` : "Nuevo registro"}
            </PanelHeader>
            <PanelBody>
              <div className="row mb-md-4">
                <div className="col-md-5 col-12">
                  <label className="form-label">Razon social que cancela:</label>
                  <input
                    disabled
                    type="text"
                    className="form-control fs-15px"
                    placeholder="Razon social"
                    name="razonsocial"
                    value={pago?.Tercero?.nombre || comprobantes?.[0]?.Tercero?.nombre || "-"}
                  />
                </div>

                <div className="col-12 col-md-2 mb-2">
                  <label className="form-label">Fecha cobro:</label>
                  <DatePicker
                    dateFormat="dd/MM/yyyy"
                    locale="es"
                    className="form-control"
                    selected={
                      typeof formData.fecha_pago === "string"
                        ? parseISO(formData.fecha_pago)
                        : formData.fecha_pago instanceof Date
                        ? formData.fecha_pago
                        : null
                    }
                    onChange={(date) =>
                      setFormData((prev) => ({
                        ...prev,
                        fecha_pago: date ? format(date, "yyyy-MM-dd") : "",
                      }))
                    }
                  />
                </div>

                <div className="col-12 col-md-2 mb-2">
                  <label className="form-label">Tipo de pago</label>
                  <select className="form-select" onChange={handleInputChange} name="id_tipopago" value={formData.id_tipopago}>
                    {tipo_pago.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-2 mb-2">
                  <label className="d-flex align-items-center fs-13px">Importe:</label>
                  <input type="number" className="form-control fs-15px" placeholder="$ 0.00" name="monto" value={formData.monto} disabled />
                </div>

                <div className="col-12 col-md-7 mb-2">
                  <label className="d-flex align-items-center fs-13px">Referencia:</label>
                  <input
                    type="text"
                    className="form-control fs-15px"
                    placeholder="Referencia"
                    value={formData.referencia}
                    onChange={handleInputChange}
                    name="referencia"
                  />
                </div>
              </div>

              <hr />

              <div className="table-responsive">
                <table className="table table-thead-sticky table-tfoot-sticky table-tbody-bordered table-px-10px table-py-4px table-sm table-striped text-nowrap mb-0">
                  <thead>
                    <tr className="text-nowrap">
                      <th>#</th>
                      <th>ID</th>
                      <th>NUMERO DE CONTROL</th>
                      <th>COMPROBANTE</th>
                      <th>FECHA</th>
                      <th>IMPORTE DE FACTURA</th>
                      <th>MONTO PENDIENTE</th>
                      <th>MONTO ABONO</th>
                      <th width="10%"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {comprobantes?.map((item, index) => {
                      const abono = formData.comprobantesPagados.find((ab) => ab.id_comprobante === item.id) || {};

                      console.log(item);
                      return (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.id}</td>
                          <td>
                            <Link to={`/venta/comprobante/${item.id}`}>{item.numeroControl}</Link>{" "}
                          </td>
                          <td>
                            <Link to={`/venta/comprobante/${item.id}`}>{item.codigoGeneracion}</Link>{" "}
                          </td>
                          <td>{item.fecEmi}</td>

                          <td>{item.totalPagar?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                          <td>{item.pendiente?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>

                          <td>
                            <input
                              type="number"
                              className={`form-control form-control-sm `}
                              placeholder="$ 0.00"
                              name="monto"
                              value={abono?.monto_aplicado ?? ""}
                              onChange={(e) => {
                                const valor = parseFloat(e.target.value || 0);

                                const updated = formData.comprobantesPagados.map((cp) =>
                                  cp.id_comprobante === item.id ? { ...cp, monto_aplicado: valor } : cp
                                );

                                const montoTotal = updated.reduce((acc, cp) => acc + (cp.monto_aplicado || 0), 0);

                                setFormData((prev) => ({
                                  ...prev,
                                  monto: montoTotal,
                                  comprobantesPagados: updated,
                                }));
                              }}
                            />
                          </td>
                          <td>
                            <button className="btn btn-danger btn-xs" onClick={() => deleteItem(item.id)}>
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </PanelBody>

            <PanelFooter className="text-center border-1">
              <a onClick={() => navigate(-1)} className="btn btn-default btn-lg ms-5px mb-2">
                Atras
              </a>
              {isEditing && (
                <a onClick={eliminarRegistroDialog} className="btn btn-danger btn-lg ms-5px mb-2">
                  Borrar Registro
                </a>
              )}

              <button onClick={handleSave} className="btn btn-primary btn-lg ms-5px mb-2" disabled={isPendingSavePago}>
                {isPendingSavePago ? "Guardando..." : "Guardar Registro"}
              </button>
            </PanelFooter>
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default Pago;
